import type { ISRNote, ISRNotesDocument } from '../types';

export interface ISRNotesParseResult {
  document: ISRNotesDocument;
  parseError: boolean;
  sourceField: 'notes' | 'isr_notes' | 'notes_json' | null;
}

const NOTES_FIELDS = ['notes', 'isr_notes', 'notes_json'] as const;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);

const hasExactKeys = (
  value: Record<string, unknown>,
  required: readonly string[],
  optional: readonly string[] = [],
) => {
  const allowed = new Set([...required, ...optional]);
  return required.every((key) => Object.prototype.hasOwnProperty.call(value, key)) &&
    Object.keys(value).every((key) => allowed.has(key));
};

export const createEmptyISRNotesDocument = (): ISRNotesDocument => ({
  schema_version: 1,
  notes: [],
});

const readOptionalText = (value: unknown): string | null | undefined => {
  if (value === null) return null;
  if (typeof value !== 'string') return undefined;
  return value;
};

const normalizeNote = (value: unknown): ISRNote | null => {
  if (!isRecord(value)) return null;

  const idValue = value.id;
  const bodyValue = value.body ?? value.content ?? value.text;
  const createdAtValue = value.created_at ?? value.createdAt ?? value.timestamp;
  if (
    (typeof idValue !== 'string' && typeof idValue !== 'number') ||
    typeof bodyValue !== 'string' ||
    typeof createdAtValue !== 'string' ||
    String(idValue).trim().length === 0 ||
    bodyValue.trim().length === 0 ||
    createdAtValue.trim().length === 0
  ) {
    return null;
  }

  const createdByValue = value.created_by ?? value.createdBy;
  const createdByNameValue = value.created_by_name ?? value.createdByName;
  const title = readOptionalText(value.title);
  const icon = readOptionalText(value.icon);
  const activityType = value.activity_type;
  const createdBy = isRecord(createdByValue)
    ? readOptionalText(createdByValue.id)
    : readOptionalText(createdByValue);
  const createdByName = isRecord(createdByValue)
    ? readOptionalText(createdByValue.name)
    : readOptionalText(createdByNameValue);

  const createdByWasInvalid =
    (isRecord(createdByValue)
      ? 'id' in createdByValue && createdBy === undefined
      : createdByValue !== undefined && createdBy === undefined);
  const createdByNameWasInvalid =
    (isRecord(createdByValue)
      ? 'name' in createdByValue && createdByName === undefined
      : createdByNameValue !== undefined && createdByName === undefined);
  const titleWasInvalid = value.title !== undefined && title === undefined;
  const iconWasInvalid = value.icon !== undefined && icon === undefined;
  // The activity_type allowlist must match the union in types.ts (ISRNote) PLUS
  // 'interaction_summary' (written by ISRWorkspace's Interaction Summary tab —
  // see handleSaveSummary). A note with any other activity_type is treated as
  // malformed so genuinely corrupt data still surfaces a parse error, but the
  // values the system actually produces are accepted. Without this, a lead
  // whose notes JSON contains an interaction_summary entry (or magnet_sent)
  // trips parseError → "Saving blocked — notes need recovery" even though the
  // JSON is perfectly valid.
  const ALLOWED_ACTIVITY_TYPES = new Set(['note', 'status_change', 'magnet_sent', 'interaction_summary']);
  const activityTypeWasInvalid = activityType !== undefined && activityType !== null &&
    !ALLOWED_ACTIVITY_TYPES.has(activityType as string);
  if (createdByWasInvalid || createdByNameWasInvalid || titleWasInvalid || iconWasInvalid || activityTypeWasInvalid) return null;

  return {
    id: String(idValue),
    body: bodyValue,
    created_at: createdAtValue,
    ...(createdBy !== undefined ? { created_by: createdBy } : {}),
    ...(createdByName !== undefined ? { created_by_name: createdByName } : {}),
    ...(title !== undefined ? { title } : {}),
    ...(icon !== undefined ? { icon } : {}),
    ...(activityType !== undefined ? { activity_type: activityType as ISRNote['activity_type'] } : {}),
  };
};

const decodeJSON = (value: unknown): unknown => {
  let decoded = value;
  // Some database integrations return a JSON column as a JSON-encoded string.
  for (let depth = 0; depth < 2 && typeof decoded === 'string'; depth += 1) {
    const trimmed = decoded.trim();
    if (!trimmed) return createEmptyISRNotesDocument();
    decoded = JSON.parse(trimmed);
  }
  return decoded;
};

export const isISRNotesDocument = (value: unknown): value is ISRNotesDocument => {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, ['schema_version', 'notes']) ||
    value.schema_version !== 1 ||
    !Array.isArray(value.notes)
  ) {
    return false;
  }
  return value.notes.every(
    (note) =>
      isRecord(note) &&
      hasExactKeys(note, ['id', 'body', 'created_at'], ['created_by', 'created_by_name', 'title', 'icon', 'activity_type']) &&
      normalizeNote(note) !== null,
  );
};

export const parseISRNotes = (value: unknown): ISRNotesParseResult => {
  try {
    const decoded = decodeJSON(value);
    if (!isRecord(decoded) || decoded.schema_version !== 1 || !Array.isArray(decoded.notes)) {
      return { document: createEmptyISRNotesDocument(), parseError: true, sourceField: null };
    }

    const notes = decoded.notes.map(normalizeNote);
    if (notes.some((note) => note === null)) {
      return { document: createEmptyISRNotesDocument(), parseError: true, sourceField: null };
    }

    return {
      document: { schema_version: 1, notes: notes as ISRNote[] },
      parseError: false,
      sourceField: null,
    };
  } catch {
    return { document: createEmptyISRNotesDocument(), parseError: true, sourceField: null };
  }
};

/**
 * Read the notes document across response envelopes used by get_lead.
 * Canonical `notes` wins, followed by the two supported legacy aliases.
 */
export const parseISRNotesFromRecord = (value: unknown): ISRNotesParseResult => {
  if (!isRecord(value)) {
    return { document: createEmptyISRNotesDocument(), parseError: false, sourceField: null };
  }

  const nestedRecords = [
    value,
    isRecord(value.lvl2) ? value.lvl2 : null,
    isRecord(value.lvl1_person) ? value.lvl1_person : null,
  ].filter((record): record is Record<string, unknown> => record !== null);

  let sawMalformedValue = false;
  for (const field of NOTES_FIELDS) {
    for (const record of nestedRecords) {
      if (!(field in record) || record[field] === null || record[field] === undefined) continue;
      const raw = record[field];
      // eslint-disable-next-line no-console
      if (typeof raw === 'string') console.log('🔍 ISR_NOTES_DEBUG: field=%s, raw_preview=%s, len=%d', field, raw.slice(0, 120), raw.length);
      // Skip plain strings — they're not JSON and not ISR notes documents.
      // A plain string would fail JSON.parse in decodeJSON and trigger the
      // catch block, returning parseError: true. This causes "Saving blocked"
      // on leads whose lvl2/lvl1_person has a `notes` field that's actually
      // a summary or other text.
      if (typeof raw === 'string' && !raw.trim().startsWith('{')) {
        // eslint-disable-next-line no-console
        console.log('🔍 ISR_NOTES_DEBUG: skipped plain string field=%s', field);
        continue;
      }
      const parsed = parseISRNotes(raw);
      if (!parsed.parseError) return { ...parsed, sourceField: field };
      sawMalformedValue = true;
    }
  }

  return {
    document: createEmptyISRNotesDocument(),
    parseError: sawMalformedValue,
    sourceField: null,
  };
};

export const appendISRNote = (
  document: ISRNotesDocument,
  note: ISRNote,
): ISRNotesDocument => {
  if (!isISRNotesDocument(document) || normalizeNote(note) === null) {
    throw new TypeError('Cannot append an invalid SDR note document or note');
  }
  return { schema_version: 1, notes: [...document.notes, { ...note }] };
};
